/**
 * Logs (Self-Service Debugging) page component.
 * Allows users to look up traces by ID.
 *
 * @module pages/LogsPage
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Search, Activity } from 'lucide-react';
import { Button } from '@/components/ui';
import { PageHeader } from '@/components/layout';

/**
 * Logs page component - trace ID lookup for self-service debugging
 */
export function LogsPage() {
  const navigate = useNavigate();
  const [traceIdSearch, setTraceIdSearch] = useState('');

  const handleTraceIdSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (traceIdSearch.trim()) {
      navigate(`/logs/traces/${encodeURIComponent(traceIdSearch.trim())}`);
    }
  };

  return (
    <div className="p-6">
      <PageHeader title="Debug Logs" description="Look up API request traces by ID" />

      {/* Help section */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-lg">
        <div className="flex items-start gap-3">
          <Activity className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">
              How to use Debug Logs
            </h4>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              Enter a trace ID to view all log events for that request. A trace represents a
              complete request flow through the ADC system. You can find trace IDs in error
              responses or in your application logs.
            </p>
          </div>
        </div>
      </div>

      {/* Trace ID Search */}
      <div className="mt-8">
        <form onSubmit={handleTraceIdSearch} className="max-w-2xl">
          <label
            htmlFor="traceId"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Trace ID
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="traceId"
                type="text"
                value={traceIdSearch}
                onChange={(e) => setTraceIdSearch(e.target.value)}
                placeholder="Enter trace ID (e.g., 4bf92f3577b34da6a3ce929d0e0e4736)"
                className="w-full pl-11 pr-4 py-3 text-base border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white dark:placeholder-gray-500 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <Button type="submit" disabled={!traceIdSearch.trim()} className="px-6">
              Look Up
            </Button>
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Trace IDs are 32-character hexadecimal strings that uniquely identify a request.
          </p>
        </form>
      </div>

      {/* Tips section */}
      <div className="mt-12 max-w-2xl">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
          Where to find trace IDs
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-[#161616] rounded-lg">
            <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 shrink-0">
              1
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                API Error Responses
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Error responses include a{' '}
                <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded-sm">
                  traceId
                </code>{' '}
                field in the response body. It may be in W3C traceparent format (e.g.,{' '}
                <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded-sm">
                  00-4bf9...4736-a3ce...0e47-01
                </code>
                ) — copy only the 32-char hex segment between the first and second{' '}
                <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded-sm">
                  -
                </code>
                .
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-[#161616] rounded-lg">
            <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 shrink-0">
              2
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">SDK Logs</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ADC SDKs log the trace ID for each request when debug logging is enabled.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
